import React from "react";
import { toast } from "sonner";
import { Database, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionPanel, Field } from "@/views/gooseberry/shared";
import { DATABASE_TYPES } from "@/data/gooseBerryData";

export function CustomDatabasePanel({
  customText,
  setCustomText,
  customType,
  setCustomType,
  customFileName,
  setCustomFileName,
  setCustomFileContent,
  language = "en",
}) {
  const t = language === "nl" ? {
    fileError: "Alleen .txt-, .md-, .csv- en .json-bestanden worden ondersteund",
    title: "Eigen database",
    help: "Optioneel: plak researchnotities of upload een klein bestand (.txt, .md, .csv, .json). Dit wordt behandeld als door jou aangeleverde context, niet als geverifieerd bewijs.",
    description: "Voeg je eigen researchnotities of een bestand toe als extra context.",
    dataType: "Datatype",
    paste: "Plak researchcontent",
    placeholder: "Belangrijke quotes, bevindingen, patronen...",
    upload: "Upload .txt / .md / .csv / .json",
    remove: "Verwijder geupload bestand",
  } : {
    fileError: "Only .txt, .md, .csv, .json files are supported",
    title: "Custom database",
    help: "Optional: paste research notes or upload a small file (.txt, .md, .csv, .json). Treated as user-provided context, not verified evidence.",
    description: "Add your own research notes or a file as extra context.",
    dataType: "Data type",
    paste: "Paste research content",
    placeholder: "Key quotes, findings, patterns...",
    upload: "Upload .txt / .md / .csv / .json",
    remove: "Remove uploaded file",
  };
  const onCustomFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = /\.(txt|md|csv|json)$/i.test(file.name);
    if (!ok) {
      toast.error(t.fileError);
      return;
    }
    const text = await file.text();
    setCustomFileName(file.name);
    setCustomFileContent(text.slice(0, 200000));
  };
  return (
    <SectionPanel
      number="4"
      title={t.title}
      optional
      icon={<Database className="h-4 w-4" />}
      help={t.help}
      description={t.description}
      testid="synberry-customdb-card"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label={t.dataType} optional testid="synberry-customdb-type">
          <Select value={customType} onValueChange={setCustomType}>
            <SelectTrigger className="rounded-xl" data-testid="synberry-customdb-type-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATABASE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label={t.paste} optional testid="synberry-customdb-text">
            <Textarea value={customText} onChange={(e) => setCustomText(e.target.value)} rows={3} placeholder={t.placeholder} className="rounded-xl" data-testid="synberry-customdb-text-field" />
          </Field>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="file" accept=".txt,.md,.csv,.json" onChange={onCustomFile} className="hidden" data-testid="synberry-customdb-file-input" />
          <Button asChild variant="outline" className="rounded-xl">
            <span><FileText className="h-4 w-4 mr-1" /> {t.upload}</span>
          </Button>
        </label>
        {customFileName && (
          <div className="flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1 text-xs" data-testid="synberry-customdb-file-chip">
            <FileText className="h-3.5 w-3.5" /> {customFileName}
            <button
              onClick={() => { setCustomFileName(""); setCustomFileContent(""); }}
              className="text-black/70 hover:text-black"
              data-testid="synberry-customdb-file-remove"
              aria-label={t.remove}
            ><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
    </SectionPanel>
  );
}
